/**
 * 1.2.29: YUIChat 项目 - 账号中心页面
 * 显示用户信息，支持修改用户名和密码
 * 1.3.74: 添加订阅与套餐区块
 * 1.4.1: 重构为 Tab 形式，整合成员管理功能
 * 1.4.2: 添加企业信息 Tab，成员用户显示所属企业信息
 * 1.4.5: 修复 Owner 也能看到企业信息 Tab
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, UserCircle, CheckCircle, User, Crown, Users, Building2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getCurrentOrganization } from '../services/organizationService';
import { isSupabaseAvailable } from '../lib/supabase';
import { BasicInfoTab, SubscriptionTab, MembersTab, OrganizationInfoTab } from '../components/account';
import type { MemberRole } from '../types/organization';

// Tab 类型定义
type TabType = 'basic' | 'subscription' | 'members' | 'organization';

// Tab 配置 - 根据角色动态显示
interface TabConfig {
  id: TabType;
  label: string;
  icon: React.ElementType;
  ownerOnly?: boolean;      // 仅所有者可见
  memberOnly?: boolean;     // 仅成员可见
  businessOnly?: boolean;   // 仅企业组织可见
}

const allTabs: TabConfig[] = [
  { id: 'basic', label: 'basicInfo', icon: User },
  { id: 'organization', label: 'organizationInfo', icon: Building2, businessOnly: true }, // 1.4.5: 移除 memberOnly，Owner 也需要查看企业信息
  { id: 'subscription', label: 'subscriptionAndPlan', icon: Crown, ownerOnly: true }, // 1.4.2: member 不显示订阅 Tab
  { id: 'members', label: 'memberManagement', icon: Users, ownerOnly: true, businessOnly: true },
];

export function AccountCenterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // 1.4.1: Tab 状态
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [isBusinessOrg, setIsBusinessOrg] = useState(false);
  const [userRole, setUserRole] = useState<MemberRole | null>(null);
  const [isLoadingOrg, setIsLoadingOrg] = useState(true);
  
  // 1.3.79: 支付成功提示状态（只显示一次）
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

  useEffect(() => {
    // 检查组织类型
    checkOrganization();
    
    // 检查 Stripe Checkout 回调状态
    const checkoutStatus = searchParams.get('checkout');
    if (checkoutStatus === 'success') {
      setShowPaymentSuccess(true);
      setActiveTab('subscription');
      // 清除 URL 参数
      window.history.replaceState({}, '', window.location.pathname);
    } else if (checkoutStatus === 'canceled') {
      toast.error(t('paymentCanceled', '支付已取消。'));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // 1.4.2: 根据组织类型和角色设置初始 Tab
  // 1.4.5: 修复 organization hash 对 owner 也生效
  useEffect(() => {
    if (isLoadingOrg) return;
    
    const hash = location.hash.replace('#', '');
    if (hash === 'members' && isBusinessOrg && userRole === 'owner') {
      setActiveTab('members');
    } else if (hash === 'organization' && isBusinessOrg) {
      // 1.4.5: Owner 和 Member 都可以访问 organization Tab
      setActiveTab('organization');
    } else if (hash === 'subscription') {
      setActiveTab('subscription');
    }
  }, [isLoadingOrg, isBusinessOrg, userRole, location.hash]);

  // 检查组织类型和用户角色
  const checkOrganization = async () => {
    if (!isSupabaseAvailable) {
      setIsLoadingOrg(false);
      return;
    }
    
    try {
      const { organization } = await getCurrentOrganization();
      if (organization) {
        // 检查是否是企业组织
        setIsBusinessOrg(organization.org_type === 'business');
        // 1.4.2: 获取用户角色
        setUserRole(organization.role);
      }
    } catch (error) {
      console.error('Error checking organization:', error);
    } finally {
      setIsLoadingOrg(false);
    }
  };

  // 1.4.2: 根据组织类型和用户角色过滤可见的 Tab
  const visibleTabs = allTabs.filter(tab => {
    // 非企业组织，过滤掉企业专属 Tab
    if (tab.businessOnly && !isBusinessOrg) return false;
    // 仅所有者可见的 Tab
    if (tab.ownerOnly && userRole !== 'owner') return false;
    // 仅成员可见的 Tab
    if (tab.memberOnly && userRole !== 'member') return false;
    return true;
  });

  // 切换 Tab
  const handleTabChange = (tabId: TabType) => {
    setActiveTab(tabId);
    // 更新 URL hash（可选，方便直接链接）
    window.history.replaceState({}, '', `${window.location.pathname}#${tabId}`);
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-6">
        {/* 返回按钮 */}
        <button
          onClick={() => navigate('/app')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('back', '返回')}</span>
        </button>
        
        {/* 页面标题 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <UserCircle className="w-8 h-8 text-primary" />
            {t('accountCenter', '账号中心')}
          </h1>
          <p className="text-gray-600 mt-2">{t('accountCenterDescription', '管理您的个人信息和账号设置')}</p>
        </div>
        
        {/* 支付成功提示 */}
        {showPaymentSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
            <div>
              <p className="text-green-800 font-medium">{t('paymentSuccess', '支付成功！')}</p>
              <p className="text-green-600 text-sm">{t('subscriptionActivated', '订阅已激活，您可以使用所有功能了。')}</p>
            </div>
          </div>
        )}
        
        {/* Tab 导航 */}
        <div className="mb-6">
          <div className="flex border-b border-gray-200">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-all ${
                    isActive
                      ? 'text-primary border-primary'
                      : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t(tab.label, 
                  tab.label === 'basicInfo' ? '基本信息' : 
                  tab.label === 'subscriptionAndPlan' ? '订阅与套餐' : 
                  tab.label === 'memberManagement' ? '成员管理' :
                  tab.label === 'organizationInfo' ? '企业信息' : tab.label
                )}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Tab 内容区域 */}
        <div className="min-h-[400px]">
          {activeTab === 'basic' && <BasicInfoTab />}
          {/* 1.4.5: Owner 和 Member 都可以查看企业信息 */}
          {activeTab === 'organization' && isBusinessOrg && <OrganizationInfoTab />}
          {activeTab === 'subscription' && <SubscriptionTab />}
          {activeTab === 'members' && isBusinessOrg && userRole === 'owner' && <MembersTab />}
        </div>
      </div>
    </div>
  );
}
